import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FiRefreshCw } from "react-icons/fi";
import { workflowService } from "../../services/workflowService";
import { Workflow } from "../../types/workflow";
import { useSubscription } from "../../hooks/useSubscription";
import UpgradeBanner from "../common/UpgradeBanner";

// Type-safe icon wrapper
const RefreshIcon = FiRefreshCw as React.FC<React.SVGProps<SVGSVGElement>>;

const ActiveWorkflowsSidebar: React.FC = () => {
  const navigate = useNavigate();
  const { isFree: isFreePlan, loading: subscriptionLoading } = useSubscription();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const MAX_DISPLAY = 3;

  const loadWorkflows = useCallback(async () => {
    try {
      setError(null);
      const allWorkflows = await workflowService.getUserWorkflows();
      
      // Filter: only schedule_enabled workflows (exclude paused)
      const activeWorkflows = allWorkflows.filter(
        (w) => w.schedule_enabled === true
      );

      // Sort by next_execution date (soonest first)
      const sorted = activeWorkflows.sort((a, b) => {
        if (!a.next_execution) return 1;
        if (!b.next_execution) return -1;
        return new Date(a.next_execution).getTime() - new Date(b.next_execution).getTime();
      });

      setWorkflows(sorted);
    } catch (err) {
      console.error("Failed to load active workflows:", err);
      setError("Failed to load workflows");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (subscriptionLoading || isFreePlan) return;

    loadWorkflows();

    // Auto-refresh every 60 seconds (industry best practice)
    const interval = setInterval(() => {
      loadWorkflows();
    }, 60000);

    return () => clearInterval(interval);
  }, [loadWorkflows, isFreePlan, subscriptionLoading]);

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "Not Scheduled";
    
    const date = new Date(dateString);
    const month = date.toLocaleDateString("en-US", { month: "short" });
    const day = date.getDate();
    
    return `${month} ${day}`;
  };

  const handleWorkflowClick = (workflowId: string) => {
    navigate(`/email-updates?tab=workflows&workflowId=${workflowId}`);
  };

  const handleCreateWorkflow = () => {
    navigate("/updates/new");
  };

  const handleViewAll = () => {
    navigate("/email-updates?tab=workflows");
  };

  const displayWorkflows = workflows.slice(0, MAX_DISPLAY);
  const hasMore = workflows.length > MAX_DISPLAY;

  // For free users, show upgrade banner instead of workflows
  if (isFreePlan) {
    return (
      <div className="su-active-workflows-section">
        <div className="su-active-workflows-header">
          ACTIVE WORKFLOWS
        </div>
        <div className="su-sidebar-upgrade-banner">
          <UpgradeBanner
            title="Unlock Automations"
            subtitle="Set up recurring updates"
            buttonText="UPGRADE"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="su-active-workflows-section">
      {/* Section Header */}
      <div className="su-active-workflows-header">
        ACTIVE WORKFLOWS
      </div>

      {/* Loading State */}
      {loading && (
        <div className="su-active-workflows-loading">
          <RefreshIcon className="su-active-workflows-spinner" />
          <span>Loading workflows...</span>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="su-active-workflows-error">
          <p>{error}</p>
          <button
            onClick={loadWorkflows}
            className="su-active-workflows-retry"
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && workflows.length === 0 && (
        <div className="su-active-workflows-empty">
          <p className="su-active-workflows-empty-text">
            No active workflows
          </p>
          <button
            onClick={handleCreateWorkflow}
            className="su-active-workflows-create-btn"
          >
            Create Workflow
          </button>
        </div>
      )}

      {/* Workflows List */}
      {!loading && !error && workflows.length > 0 && (
        <>
          <div className="su-active-workflows-list">
            {displayWorkflows.map((workflow) => (
              <button
                key={workflow.id}
                onClick={() => handleWorkflowClick(workflow.id)}
                className="su-active-workflow-item"
              >
                <div className="su-active-workflow-icon">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6 12L10 8L6 4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="su-active-workflow-content">
                  <div className="su-active-workflow-name">
                    {workflow.name}
                  </div>
                  <div className="su-active-workflow-date">
                    Next: {formatDate(workflow.next_execution)}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* View All Link */}
          {hasMore && (
            <button
              onClick={handleViewAll}
              className="su-active-workflows-view-all"
            >
              View All ({workflows.length})
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default ActiveWorkflowsSidebar;

